/**
 * Combine the two sources of activity steps for one assistant turn.
 *
 * Normally the backend types every step and the timeline comes from it alone.
 * When a producer leaks its raw transcript instead, `utils/agentScaffolding`
 * salvages steps out of that text — and those are the only trail the turn has,
 * because a producer that failed to parse its own decision never emitted typed
 * steps for it. Both can be present (a status ping from the backend plus a
 * salvaged thought), so they merge rather than one replacing the other.
 */

// Content can be long; comparing a prefix is enough to spot the same step
// arriving from both sources, and avoids paying for full-string compares on
// every render. Tool name and status are part of the identity — salvaged tool
// steps carry their payload in `args` with an empty content, so two different
// tools would otherwise collide on `tool:` and one would be dropped.
const identity = (step) =>
  [
    step?.type || '',
    step?.tool || '',
    step?.status || '',
    step?.args ? JSON.stringify(step.args).slice(0, 120) : '',
    String(step?.content || '').slice(0, 120),
  ].join('|');

/**
 * @param {Array|null} primary - typed steps from the backend
 * @param {Array|null} salvaged - steps recovered from leaked scaffolding
 * @returns {Array|null} merged steps in order, or null when there are none (the
 *   callers treat null as "leave whatever the message already had").
 */
export const mergeActivitySteps = (primary, salvaged) => {
  const base = Array.isArray(primary) ? primary.filter(Boolean) : [];
  const extra = Array.isArray(salvaged) ? salvaged.filter(Boolean) : [];
  if (!extra.length) return base.length ? base : null;
  const seen = new Set(base.map(identity));
  const merged = [...base, ...extra.filter((s) => !seen.has(identity(s)))];
  return merged.length ? merged : null;
};

/**
 * Merge polled steps into one bot message, against the list that message
 * currently holds.
 *
 * Pure so it can run inside a React state updater: polled steps and the stream's
 * own steps land on the same field concurrently, and merging from a value read
 * before the updater ran would apply a stale snapshot and drop whatever the
 * stream committed in between. Returns the SAME array reference when there is
 * nothing to change, so React can skip the re-render.
 *
 * @param {Array} messages - current chat list
 * @param {object} target - {botMessageId, messageId, steps}
 * @returns {Array} messages, with the matched bot message's reasoning_steps merged
 */
export const applyStepsToMessage = (messages, { botMessageId, messageId, steps } = {}) => {
  if (!Array.isArray(messages) || !Array.isArray(steps) || steps.length === 0) return messages;
  const idx = messages.findIndex(
    (msg) => msg && msg.author_id === 0 && (msg.id === botMessageId || (messageId && msg.id === messageId))
  );
  if (idx === -1) return messages;
  const merged = mergeActivitySteps(messages[idx].reasoning_steps, steps) || [];
  const updated = [...messages];
  updated[idx] = { ...messages[idx], reasoning_steps: merged };
  return updated;
};

/**
 * Steps to show for the turn currently in flight.
 *
 * The bot card that normally hosts the timeline is hidden until `answer_type`
 * arrives, so while a turn runs the timeline is rendered separately — this picks
 * what it should show. Returns [] once `answer_type` lands, because from that
 * moment the card renders and owns the timeline; showing both would double it.
 *
 * @param {Array} messages - the live chat list
 * @returns {Array} steps of the most recent assistant message, or []
 */
export const liveActivityStepsFor = (messages) => {
  if (!Array.isArray(messages)) return [];
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const msg = messages[i];
    if (msg && msg.author_id === 0) {
      if (msg.answer_type) return [];
      return Array.isArray(msg.reasoning_steps) ? msg.reasoning_steps : [];
    }
  }
  return [];
};

/**
 * One-line gist of a step, for a row label or the live status line.
 *
 * A tool step reads as its tool name rather than its content, because the
 * content is the `[tool X: ok]` marker — machinery, and the status line's own
 * cleaner discards it, which would leave the line blank.
 *
 * @param {object} step
 * @returns {string} '' when there is nothing worth showing
 */
export const stepLabel = (step) => {
  if (!step) return '';
  if (step.type === 'tool') return step.tool || 'Tool';
  const content = typeof step.content === 'string' ? step.content : '';
  return content.split('\n').find((l) => l.trim()) || '';
};
